import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import ChatMessage from '../components/chat/ChatMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faCheck, faTimes, faComments } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { PageHeader } from '../design-system';

export default function ChatDetail() {
  const { chatId } = useParams();
  const [searchParams] = useSearchParams();
  const autoSend = searchParams.get('autoSend') === 'true';
  const navigate = useNavigate();
  const { user } = useAuth();
  const { family } = useFamily();
  
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');

  // Load conversation
  useEffect(() => {
    if (!chatId) return;

    const loadConversation = async () => {
      try {
        const convDoc = await getDoc(doc(db, 'conversations', chatId));
        if (convDoc.exists()) {
          setConversation({
            id: convDoc.id,
            ...convDoc.data()
          });
        } else {
          toast.error('Conversation not found');
          navigate('/chats');
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
        toast.error('Failed to load conversation');
        navigate('/chats');
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
  }, [chatId, navigate]);

  // Auto-send to AI when autoSend is true
  useEffect(() => {
    if (autoSend && conversation?.messages?.length > 0 && !sending) {
      // Get the last user message
      const lastMessage = conversation.messages[conversation.messages.length - 1];
      
      if (lastMessage.role === 'user') {
        // Auto-send to AI
        sendToAI(lastMessage.content);
      }
    }
  }, [autoSend, conversation, sending]);

  async function sendToAI(message) {
    if (!message || sending) return;

    setSending(true);

    try {
      const chatWithClaude = httpsCallable(functions, 'chatWithClaude');

      const familyContext = {
        familyId: family?.id || null,
        name: family?.name || 'Your Family',
        kids: family?.kids || [],
        onboarding: family?.onboarding || {},
      };

      const result = await chatWithClaude({
        conversationHistory: conversation.messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
        familyContext,
        currentProject: null,
        conversationId: conversation.id,
      });

      // Update conversation with AI response
      const assistantMessage = {
        role: 'assistant',
        content: result.data.message,
        timestamp: new Date(),
      };

      const updatedMessages = [...conversation.messages, assistantMessage];

      await updateDoc(doc(db, 'conversations', conversation.id), {
        messages: updatedMessages,
        updatedAt: serverTimestamp(),
      });

      setConversation({
        ...conversation,
        messages: updatedMessages
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to get AI response. Please try again.');
    } finally {
      setSending(false);
    }
  }

  const handleStartEdit = () => {
    setIsEditingTitle(true);
    setEditTitle(conversation.title || '');
  };

  const handleCancelEdit = () => {
    setIsEditingTitle(false);
    setEditTitle('');
  };

  const handleSaveEdit = async () => {
    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle) {
      toast.error('Title cannot be empty');
      return;
    }

    try {
      await updateDoc(doc(db, 'conversations', conversation.id), {
        title: trimmedTitle,
        updatedAt: serverTimestamp(),
      });
      setConversation({
        ...conversation,
        title: trimmedTitle
      });
      toast.success('Conversation renamed');
      setIsEditingTitle(false);
    } catch (error) {
      console.error('Error renaming conversation:', error);
      toast.error('Failed to rename conversation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-secondary">Loading conversation...</div>
      </div>
    );
  }

  if (!conversation) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background pt-14 pb-20 md:pt-0 md:pb-0">
      {isEditingTitle ? (
        <div className="bg-white border-b border-border sticky top-0 z-40 md:z-[100] mobile-sticky-header">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/chats')}
                className="text-xl sm:text-2xl text-text-secondary hover:text-text-primary transition-colors bg-transparent border-none cursor-pointer flex-shrink-0 touch-target min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95"
                aria-label="Go back"
              >
                ←
              </button>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveEdit();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                className="flex-1 px-3 py-2 text-lg sm:text-xl md:text-2xl font-semibold text-text-primary border border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
              <button
                onClick={handleSaveEdit}
                className="text-green-600 hover:text-green-700 transition-colors p-2 touch-target min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Save"
              >
                <FontAwesomeIcon icon={faCheck} />
              </button>
              <button
                onClick={handleCancelEdit}
                className="text-red-600 hover:text-red-700 transition-colors p-2 touch-target min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Cancel"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <PageHeader
          title={conversation.title || 'Conversation'}
          onBack={() => navigate('/chats')}
          actions={
            <button
              onClick={handleStartEdit}
              className="text-text-secondary hover:text-primary transition-colors p-2 touch-target min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Rename conversation"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
          }
          maxWidth="896px"
        />
      )}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Messages */}
        <div className="bg-white rounded-2xl border border-border shadow-sm p-6">
          <div className="space-y-4 min-h-[400px]">
            {conversation.messages && conversation.messages.length > 0 ? (
              conversation.messages.map((message, index) => (
                <ChatMessage 
                  key={index} 
                  message={message} 
                  isUser={message.role === 'user'} 
                />
              ))
            ) : (
              <div className="text-center text-text-secondary py-8">
                <p>No messages in this conversation</p>
              </div>
            )}
            
            {sending && (
              <div className="flex justify-start">
                <div className="bg-surface rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-text-secondary">
                    <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

